import { Group } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import { useWorkOrderTrackingCalendarComplement } from '@/core/domain/workorder/Tracking/Tracking.hook';
import { WorkOrderTrackingCalendar } from '@/core/domain/workorder/Tracking/Tracking.types';
import { ComplementOriginTranslator } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { workorderComplementColor } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWindowSize } from '@/hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  Button,
  Col,
  Descriptions,
  Divider,
  Modal,
  ModalProps,
  Row,
  Skeleton,
  Statistic,
  Typography,
} from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';

interface Props extends ModalProps {
  onCancel: () => void;
  workorder?: WorkOrderTrackingCalendar;
}

const WorkorderCalendarModal: React.FC<Props> = ({ workorder, ...props }) => {
  const intl = useIntl();
  const { isMobile } = useWindowSize();

  const complement = useWorkOrderTrackingCalendarComplement(
    workorder?.complementId,
  );

  const complementData = complement.data;

  return (
    <Modal
      {...props}
      title="Pré-Visualização do orçamento"
      destroyOnClose
      width={600}
      footer={
        <Group position="center">
          <Button
            type="primary"
            onClick={() =>
              window.open(
                `/app/movements/workOrders/${workorder?.workOrderId}/complements/${workorder?.complementId}/generalInfo`,
                '_blank',
              )
            }
          >
            Visualizar detalhes da OS
          </Button>
        </Group>
      }
    >
      <Group style={{ marginBottom: 16 }} position="between">
        {!!workorder && (
          <StatusTag
            key={workorder?.workOrderId}
            label={intl.formatMessage({
              id: `workOrders.${workorder?.status}`,
            })}
            color={workorderComplementColor[workorder?.status]}
            size="lg"
          />
        )}
        <big>
          <strong>{workorder?.complementIdentifier}</strong>
        </big>
      </Group>

      <Skeleton loading={complement.isLoading}>
        <Typography.Title level={5}>Total do orçamento</Typography.Title>

        <Row gutter={[24, 24]}>
          <Col>
            <Statistic
              title="Total"
              value={complementData?.total}
              formatter={(value) => formatterPrice(Number(value))}
            />
          </Col>

          <Col>
            <Divider type="vertical" style={{ minHeight: '100%' }} />
          </Col>

          <Col>
            <Statistic
              title="Total de peças"
              value={complementData?.partsTotal}
              formatter={(value) => formatterPrice(Number(value))}
            />
          </Col>

          <Col>
            <Divider type="vertical" style={{ minHeight: '100%' }} />
          </Col>

          <Col>
            <Statistic
              title="Total de serviços"
              value={complementData?.servicesTotal}
              formatter={(value) => formatterPrice(Number(value))}
            />
          </Col>
        </Row>

        <Divider />

        <Descriptions
          layout="horizontal"
          column={1}
          labelStyle={{ fontWeight: 'bold' }}
          size="middle"
          bordered
        >
          <Descriptions.Item label="Empresa" style={{ padding: 6 }}>
            {complementData?.companyName}
          </Descriptions.Item>

          <Descriptions.Item label="Cliente" style={{ padding: 6 }}>
            {complementData?.clientName}
          </Descriptions.Item>

          <Descriptions.Item label="Veículo" style={{ padding: 6 }}>
            {complementData?.vehicleName || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Placa" style={{ padding: 6 }}>
            {complementData?.licensePlate || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Origem" style={{ padding: 6 }}>
            {complementData?.complementOrigin
              ? ComplementOriginTranslator[complementData?.complementOrigin]
              : '-'}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Descriptions
          layout="vertical"
          column={isMobile ? 1 : 2}
          labelStyle={{ fontWeight: 'bold', margin: 6 }}
          contentStyle={{ margin: 6 }}
          size="middle"
          bordered
        >
          <Descriptions.Item label="Data de entrada" style={{ padding: 6 }}>
            {complementData?.entryDate
              ? moment(complementData.entryDate).format('LLL')
              : ''}
          </Descriptions.Item>

          <Descriptions.Item label="Data de autorização" style={{ padding: 6 }}>
            {complementData?.authorizationDate
              ? moment(complementData.authorizationDate).format('LLL')
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Saída prevista" style={{ padding: 6 }}>
            {complementData?.estimatedLeaveDate
              ? moment(complementData.estimatedLeaveDate).format('LLL')
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Data de fechamento" style={{ padding: 6 }}>
            {complementData?.closingDate
              ? moment(complementData.closingDate).format('LLL')
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Data de entrega" style={{ padding: 6 }}>
            {complementData?.returnDate
              ? moment(complementData.returnDate).format('LLL')
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Skeleton>
    </Modal>
  );
};

export default WorkorderCalendarModal;
